import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  BarChart3, Trophy, AlertTriangle, TrendingUp, TrendingDown, 
  Users, Package, RotateCcw, Filter, ChevronLeft, ChevronRight,
  Award, Medal, Star, AlertCircle, CheckCircle, Clock, User,
  Calendar, ArrowUpDown, X, FileText, History, Target, Info
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

// Performance thresholds (seuils officiels)
const PERFORMANCE_THRESHOLDS = {
  excellent: { min: 575, label: 'Très bon', color: 'green', monthly: 12100 },
  good: { min: 480, label: 'Bon', color: 'blue', monthly: 10100 },
  normal: { min: 430, label: 'Normal', color: 'orange', monthly: 9050 },
  insufficient: { min: 0, label: 'Insuffisant', color: 'red', monthly: 0 }
};

// Alert thresholds
const ALERT_THRESHOLDS = {
  ok: { max: 3, label: 'OK', color: 'green' },
  warning: { max: 6, label: 'À surveiller', color: 'orange' },
  critical: { max: Infinity, label: 'Critique', color: 'red' }
};

// Performance color mapping
const getPerformanceColor = (level) => {
  const colors = {
    excellent: 'text-green-600 bg-green-100',
    good: 'text-blue-600 bg-blue-100',
    normal: 'text-orange-600 bg-orange-100',
    insufficient: 'text-red-600 bg-red-100'
  };
  return colors[level] || 'text-gray-600 bg-gray-100';
};

const getPerformanceLabel = (level) => {
  const labels = {
    excellent: 'Très bon',
    good: 'Bon',
    normal: 'Normal',
    insufficient: 'Insuffisant'
  };
  return labels[level] || 'Inconnu';
};

const getAlertColor = (level) => {
  const colors = {
    ok: 'text-green-600 bg-green-100',
    warning: 'text-orange-600 bg-orange-100',
    critical: 'text-red-600 bg-red-100'
  };
  return colors[level] || 'text-gray-600 bg-gray-100';
};

// Helper functions for calculations
const calculatePerformanceLevel = (colis) => {
  if (colis >= 575) return 'excellent';
  if (colis >= 480) return 'good';
  if (colis >= 430) return 'normal';
  return 'insufficient';
};

const calculateAlertLevel = (reclamations) => {
  if (reclamations >= 7) return 'critical';
  if (reclamations >= 4) return 'warning';
  return 'ok';
};

const calculateScore = (colis, tauxLivraison, tauxReclamation) => {
  const excellent = 575;
  const packageScore = Math.min((colis / excellent) * 100, 100);
  const deliveryScore = tauxLivraison;
  const complaintPenalty = Math.min(tauxReclamation * 20, 100);
  const complaintScore = Math.max(100 - complaintPenalty, 0);
  
  return Math.round((packageScore * 0.5 + deliveryScore * 0.3 + complaintScore * 0.2) * 100) / 100;
};

// Podium Component
const Podium = ({ ranking, onDriverClick }) => {
  if (!ranking || ranking.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Trophy className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>Aucune donnée pour le podium</p>
      </div>
    );
  }

  const podiumOrder = [1, 0, 2];
  const podiumHeights = ['h-24', 'h-32', 'h-20'];
  const podiumColors = ['bg-gray-300', 'bg-yellow-400', 'bg-amber-600'];
  const medals = ['🥈', '🥇', '🥉'];

  return (
    <div className="flex items-end justify-center gap-4 py-6">
      {podiumOrder.map((pos, idx) => {
        const driver = ranking[pos];
        if (!driver) return null;

        return (
          <div key={pos} className="flex flex-col items-center">
            <div className="text-center mb-2">
              <span className="text-3xl">{medals[idx]}</span>
              <button 
                onClick={() => onDriverClick && onDriverClick(driver)}
                className="font-bold text-sm mt-1 truncate max-w-[120px] text-blue-600 hover:underline block mx-auto" 
                title={driver.name}
              >
                {driver.name}
              </button>
              <p className="text-xs text-gray-500">{driver.site_name}</p>
              <p className="text-lg font-bold text-blue-600">{driver.colis_livres} colis</p>
              <span className={`text-xs px-2 py-0.5 rounded-full ${getPerformanceColor(driver.performance_level)}`}>
                {getPerformanceLabel(driver.performance_level)}
              </span>
            </div>
            <div 
              className={`${podiumHeights[idx]} w-24 ${podiumColors[idx]} rounded-t-lg flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity`}
              onClick={() => onDriverClick && onDriverClick(driver)}
            >
              <span className="text-2xl font-bold text-white">{pos + 1}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Stats Summary Cards (clickable KPIs)
const StatsSummary = ({ stats, onKpiClick }) => {
  if (!stats) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <Card 
        className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-blue-300"
        onClick={() => onKpiClick && onKpiClick('livreurs')}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Livreurs actifs</p>
              <p className="text-2xl font-bold">{stats.totalLivreurs}</p>
              <p className="text-xs text-blue-500 mt-1">Cliquer pour détails →</p>
            </div>
            <Users className="w-8 h-8 text-blue-500" />
          </div>
        </CardContent>
      </Card>

      <Card 
        className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-green-300"
        onClick={() => onKpiClick && onKpiClick('colis')}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Colis</p>
              <p className="text-2xl font-bold">{stats.totalColis.toLocaleString()}</p>
              <p className="text-xs text-green-500 mt-1">Cliquer pour détails →</p>
            </div>
            <Package className="w-8 h-8 text-green-500" />
          </div>
        </CardContent>
      </Card>

      <Card 
        className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-emerald-300"
        onClick={() => onKpiClick && onKpiClick('taux')}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Taux Livraison</p>
              <p className="text-2xl font-bold">{stats.tauxLivraison}%</p>
              <p className="text-xs text-emerald-500 mt-1">Cliquer pour détails →</p>
            </div>
            <TrendingUp className="w-8 h-8 text-emerald-500" />
          </div>
        </CardContent>
      </Card>

      <Card 
        className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-orange-300"
        onClick={() => onKpiClick && onKpiClick('reclamations')}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Réclamations</p>
              <p className="text-2xl font-bold">{stats.totalReclamations}</p>
              <p className="text-xs text-orange-500 mt-1">Cliquer pour détails →</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-orange-500" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Performance Distribution
const PerformanceDistribution = ({ distribution }) => {
  if (!distribution) return null;

  const total = Object.values(distribution).reduce((a, b) => a + b, 0) || 1;
  const items = [
    { level: 'excellent', label: 'Très bon', count: distribution.excellent || 0, color: 'bg-green-500' },
    { level: 'good', label: 'Bon', count: distribution.good || 0, color: 'bg-blue-500' },
    { level: 'normal', label: 'Normal', count: distribution.normal || 0, color: 'bg-orange-500' },
    { level: 'insufficient', label: 'Insuffisant', count: distribution.insufficient || 0, color: 'bg-red-500' }
  ];

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-lg">Répartition des performances</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.level} className="flex items-center gap-3">
              <div className="w-24 text-sm font-medium">{item.label}</div>
              <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                <div 
                  className={`h-full ${item.color} transition-all duration-500`}
                  style={{ width: `${(item.count / total) * 100}%` }}
                />
              </div>
              <div className="w-12 text-right font-medium">{item.count}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Quality Ranking Table (from reclamations)
const QualityRanking = ({ driverStats, onDriverClick }) => {
  if (!driverStats || driverStats.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-yellow-500" />
            Classement Qualité (Réclamations)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-gray-500 py-4">Aucune réclamation pour cette période</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Award className="w-5 h-5 text-yellow-500" />
          Classement Qualité (Réclamations)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3 text-xs font-semibold">#</th>
                <th className="text-left p-3 text-xs font-semibold">Livreur</th>
                <th className="text-center p-3 text-xs font-semibold">Total</th>
                <th className="text-center p-3 text-xs font-semibold">Non traité</th>
                <th className="text-center p-3 text-xs font-semibold">En cours</th>
                <th className="text-center p-3 text-xs font-semibold">Traité</th>
                <th className="text-center p-3 text-xs font-semibold">Score</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {driverStats.map((driver, index) => (
                <tr 
                  key={driver.livreur_id} 
                  className={`hover:bg-gray-50 ${driver.non_traite > 0 ? 'bg-red-50' : ''}`}
                >
                  <td className="p-3 text-sm">
                    {index === 0 && <span>🥇</span>}
                    {index === 1 && <span>🥈</span>}
                    {index === 2 && <span>🥉</span>}
                    {index > 2 && <span className="text-gray-400">{index + 1}</span>}
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => onDriverClick && onDriverClick(driver.livreur_id)}
                      className="font-medium text-blue-600 hover:underline"
                    >
                      {driver.livreur_name || 'Inconnu'}
                    </button>
                  </td>
                  <td className="p-3 text-center font-semibold">{driver.total}</td>
                  <td className="p-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs ${driver.non_traite > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100'}`}>
                      {driver.non_traite}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <span className="px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-700">
                      {driver.en_cours}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                      {driver.traite + (driver.traite_livreur || 0)}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      driver.score >= 80 ? 'bg-green-100 text-green-700' :
                      driver.score >= 50 ? 'bg-orange-100 text-orange-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {driver.score}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// Full Delivery Ranking Table
const DeliveryRanking = ({ ranking, onDriverClick }) => {
  if (!ranking || ranking.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>Aucune donnée de livraison pour cette période</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="text-left p-3 font-medium text-gray-600">#</th>
            <th className="text-left p-3 font-medium text-gray-600">Livreur</th>
            <th className="text-left p-3 font-medium text-gray-600">Site</th>
            <th className="text-right p-3 font-medium text-gray-600">Colis</th>
            <th className="text-right p-3 font-medium text-gray-600">Retours</th>
            <th className="text-right p-3 font-medium text-gray-600">Non Coll.</th>
            <th className="text-right p-3 font-medium text-gray-600">Taux Livr.</th>
            <th className="text-right p-3 font-medium text-gray-600">Réclam.</th>
            <th className="text-center p-3 font-medium text-gray-600">Performance</th>
            <th className="text-center p-3 font-medium text-gray-600">Alerte</th>
            <th className="text-right p-3 font-medium text-gray-600">Score</th>
          </tr>
        </thead>
        <tbody>
          {ranking.map((driver, idx) => (
            <tr 
              key={driver.user_id}
              className={`border-b hover:bg-gray-50 cursor-pointer ${
                driver.alert_level === 'critical' ? 'bg-red-50' :
                driver.alert_level === 'warning' ? 'bg-orange-50' : ''
              }`}
              onClick={() => onDriverClick && onDriverClick(driver)}
            >
              <td className="p-3">
                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                  idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                  idx === 1 ? 'bg-gray-200 text-gray-700' :
                  idx === 2 ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {idx + 1}
                </span>
              </td>
              <td className="p-3">
                <button 
                  className="font-medium text-blue-600 hover:underline text-left"
                  onClick={(e) => { e.stopPropagation(); onDriverClick && onDriverClick(driver); }}
                >
                  {driver.name}
                </button>
              </td>
              <td className="p-3 text-gray-600">{driver.site_name}</td>
              <td className="p-3 text-right font-medium">{driver.colis_livres.toLocaleString()}</td>
              <td className="p-3 text-right text-orange-600">{driver.colis_retours}</td>
              <td className="p-3 text-right text-red-600">{driver.non_collected || 0}</td>
              <td className="p-3 text-right">{driver.taux_livraison}%</td>
              <td className="p-3 text-right">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  driver.reclamations === 0 ? 'bg-green-100 text-green-700' :
                  driver.reclamations < 4 ? 'bg-gray-100 text-gray-700' :
                  driver.reclamations < 7 ? 'bg-orange-100 text-orange-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {driver.reclamations}
                </span>
              </td>
              <td className="p-3 text-center">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPerformanceColor(driver.performance_level)}`}>
                  {getPerformanceLabel(driver.performance_level)}
                </span>
              </td>
              <td className="p-3 text-center">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAlertColor(driver.alert_level)}`}>
                  {driver.alert_level === 'ok' ? 'OK' : driver.alert_level === 'warning' ? 'Alerte' : 'Critique'}
                </span>
              </td>
              <td className="p-3 text-right">
                <span className="font-bold text-blue-600">{driver.score}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Driver Detail Modal - Fiche complète livreur
const DriverDetailModal = ({ driver, history, reclamations, loading, onClose }) => {
  if (!driver) return null;

  // Calculate totals from history
  const totalColis = history.reduce((sum, w) => sum + w.colis, 0);
  const totalRetours = history.reduce((sum, w) => sum + w.retours, 0);
  const totalNonCollected = history.reduce((sum, w) => sum + w.nonCollected, 0);
  const totalMissions = history.reduce((sum, w) => sum + w.missions, 0);
  const avgColisPerWeek = history.length > 0 ? Math.round(totalColis / history.length) : 0;
  const tauxLivraisonGlobal = totalColis > 0 
    ? Math.round(((totalColis - totalRetours) / totalColis) * 10000) / 100 
    : 0;

  // Calculate performance trend
  const trend = history.length >= 2 
    ? history[0].colis - history[1].colis 
    : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <User className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{driver.name}</h2>
                <p className="text-blue-100">{driver.site_name}</p>
                <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ${
                  driver.performance_level === 'excellent' ? 'bg-green-500' :
                  driver.performance_level === 'good' ? 'bg-blue-400' :
                  driver.performance_level === 'normal' ? 'bg-orange-500' :
                  'bg-red-500'
                }`}>
                  {getPerformanceLabel(driver.performance_level)}
                </span>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Score et Position */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-blue-600">Score Global</p>
                    <p className="text-3xl font-bold text-blue-700">{driver.score}</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-green-600">Taux Livraison</p>
                    <p className="text-3xl font-bold text-green-700">{driver.taux_livraison}%</p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-orange-600">Réclamations</p>
                    <p className="text-3xl font-bold text-orange-700">{driver.reclamations}</p>
                  </CardContent>
                </Card>
                <Card className={`${trend >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-gray-600">Tendance</p>
                    <div className="flex items-center justify-center gap-1">
                      {trend >= 0 ? (
                        <TrendingUp className="w-6 h-6 text-emerald-600" />
                      ) : (
                        <TrendingDown className="w-6 h-6 text-red-600" />
                      )}
                      <p className={`text-2xl font-bold ${trend >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                        {trend >= 0 ? '+' : ''}{trend}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Explication des performances */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5 text-blue-500" />
                    Analyse de Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium text-gray-800">📦 Productivité</p>
                      <p className="text-sm text-gray-600 mt-1">
                        Moyenne de <strong>{avgColisPerWeek} colis/semaine</strong> sur les 4 dernières semaines.
                        {avgColisPerWeek >= 575 && " Performance excellente, au-dessus de l'objectif !"}
                        {avgColisPerWeek >= 480 && avgColisPerWeek < 575 && " Bonne performance, continue ainsi !"}
                        {avgColisPerWeek >= 430 && avgColisPerWeek < 480 && " Performance correcte, mais peut s'améliorer."}
                        {avgColisPerWeek < 430 && " Performance à améliorer, en dessous de l'objectif."}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium text-gray-800">📉 Retours</p>
                      <p className="text-sm text-gray-600 mt-1">
                        <strong>{totalRetours} retours</strong> sur {totalColis} colis ({((totalRetours / totalColis) * 100 || 0).toFixed(1)}%).
                        {((totalRetours / totalColis) * 100 || 0) < 5 && " Excellent taux de retour !"}
                        {((totalRetours / totalColis) * 100 || 0) >= 5 && ((totalRetours / totalColis) * 100 || 0) < 10 && " Taux de retour acceptable."}
                        {((totalRetours / totalColis) * 100 || 0) >= 10 && " Taux de retour élevé à surveiller."}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium text-gray-800">⚠️ Réclamations</p>
                      <p className="text-sm text-gray-600 mt-1">
                        <strong>{driver.reclamations} réclamation(s)</strong> cette période.
                        {driver.reclamations === 0 && " Aucune réclamation, excellent !"}
                        {driver.reclamations > 0 && driver.reclamations < 4 && " Nombre de réclamations acceptable."}
                        {driver.reclamations >= 4 && driver.reclamations < 7 && " Nombre de réclamations à surveiller."}
                        {driver.reclamations >= 7 && " ALERTE : Trop de réclamations, action requise !"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Historique hebdomadaire */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5 text-purple-500" />
                    Historique Hebdomadaire (4 dernières semaines)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {history.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">Aucun historique disponible</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left p-3 font-medium">Semaine</th>
                            <th className="text-right p-3 font-medium">Colis</th>
                            <th className="text-right p-3 font-medium">Retours</th>
                            <th className="text-right p-3 font-medium">Non coll.</th>
                            <th className="text-right p-3 font-medium">Missions</th>
                            <th className="text-center p-3 font-medium">Performance</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {history.map((week, idx) => {
                            const perf = calculatePerformanceLevel(week.colis);
                            return (
                              <tr key={week.weekStart} className={idx === 0 ? 'bg-blue-50' : ''}>
                                <td className="p-3 font-medium">
                                  {week.weekLabel}
                                  {idx === 0 && <span className="ml-2 text-xs text-blue-600">(actuelle)</span>}
                                </td>
                                <td className="p-3 text-right font-bold">{week.colis}</td>
                                <td className="p-3 text-right text-orange-600">{week.retours}</td>
                                <td className="p-3 text-right text-red-600">{week.nonCollected}</td>
                                <td className="p-3 text-right">{week.missions}</td>
                                <td className="p-3 text-center">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPerformanceColor(perf)}`}>
                                    {getPerformanceLabel(perf)}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="bg-gray-100 font-bold">
                          <tr>
                            <td className="p-3">TOTAL</td>
                            <td className="p-3 text-right">{totalColis}</td>
                            <td className="p-3 text-right text-orange-600">{totalRetours}</td>
                            <td className="p-3 text-right text-red-600">{totalNonCollected}</td>
                            <td className="p-3 text-right">{totalMissions}</td>
                            <td className="p-3 text-center">-</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Réclamations détaillées */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    Réclamations ({reclamations.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {reclamations.length === 0 ? (
                    <div className="text-center py-4">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                      <p className="text-green-600 font-medium">Aucune réclamation</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {reclamations.slice(0, 10).map((rec) => (
                        <div 
                          key={rec.reclamation_id} 
                          className={`p-3 rounded-lg border ${
                            rec.status === 'non_traite' ? 'bg-red-50 border-red-200' :
                            rec.status === 'en_cours' ? 'bg-orange-50 border-orange-200' :
                            'bg-green-50 border-green-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{rec.reference_colis}</p>
                              <p className="text-sm text-gray-600">{rec.type_reclamation}</p>
                              <p className="text-xs text-gray-500">
                                {new Date(rec.date_reclamation).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              rec.status === 'non_traite' ? 'bg-red-100 text-red-700' :
                              rec.status === 'en_cours' ? 'bg-orange-100 text-orange-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {rec.status === 'non_traite' ? 'Non traité' :
                               rec.status === 'en_cours' ? 'En cours' : 'Traité'}
                            </span>
                          </div>
                        </div>
                      ))}
                      {reclamations.length > 10 && (
                        <p className="text-center text-gray-500 text-sm">
                          ... et {reclamations.length - 10} autres réclamations
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Seuils de référence */}
              <Card className="bg-gray-50">
                <CardContent className="p-4">
                  <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Objectifs de performance
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div className="p-2 rounded bg-green-100 text-green-800">
                      <p className="font-bold">Très bon</p>
                      <p>≥ 575/sem</p>
                      <p className="text-xs">12 100/mois</p>
                    </div>
                    <div className="p-2 rounded bg-blue-100 text-blue-800">
                      <p className="font-bold">Bon</p>
                      <p>≥ 480/sem</p>
                      <p className="text-xs">10 100/mois</p>
                    </div>
                    <div className="p-2 rounded bg-orange-100 text-orange-800">
                      <p className="font-bold">Normal</p>
                      <p>≥ 430/sem</p>
                      <p className="text-xs">9 050/mois</p>
                    </div>
                    <div className="p-2 rounded bg-red-100 text-red-800">
                      <p className="font-bold">Insuffisant</p>
                      <p>&lt; 430/sem</p>
                      <p className="text-xs">À améliorer</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
          <Button 
            onClick={() => window.location.href = `/admin/b2b/reclamations?driver=${driver.user_id}`}
            className="bg-orange-600 hover:bg-orange-700"
          >
            Voir toutes les réclamations
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Component
const AdminDriverStatistics = ({ user, activeModule = "b2b" }) => {
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('week');
  const [offset, setOffset] = useState(0);
  const [selectedSite, setSelectedSite] = useState('');
  const [sites, setSites] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [reclamationStats, setReclamationStats] = useState({ global_stats: {}, driver_stats: [] });
  const [periodLabel, setPeriodLabel] = useState('');
  
  // Driver detail modal state
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [driverDetailLoading, setDriverDetailLoading] = useState(false);
  const [driverHistory, setDriverHistory] = useState([]);
  const [driverReclamations, setDriverReclamations] = useState([]);
  
  // Detail view state (for clicking on KPIs)
  const [detailView, setDetailView] = useState(null); // 'livreurs' | 'colis' | 'taux' | 'reclamations'

  const isApprovedWorkedSchedule = (schedule) => {
    const status = String(schedule?.status || '').trim().toLowerCase();
    const validation = String(schedule?.validation_status || '').trim().toLowerCase();
    const isApproved = validation === 'approved' || validation === 'validated';
    return status === 'present' && isApproved;
  };
  // Calculate date range based on viewMode and offset
  const getDateRange = useCallback(() => {
    const now = new Date();
    let start, end;

    if (viewMode === 'week') {
      // Get current week start (Monday)
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1) + (offset * 7);
      start = new Date(now.setDate(diff));
      start.setHours(0, 0, 0, 0);
      end = new Date(start);
      end.setDate(end.getDate() + 6);
      end.setHours(23, 59, 59, 999);
    } else {
      // Get month start
      start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      end = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
      end.setHours(23, 59, 59, 999);
    }

    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
      label: viewMode === 'week' 
        ? `Semaine du ${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} au ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`
        : start.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    };
  }, [viewMode, offset]);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end, label } = getDateRange();
      setPeriodLabel(label);

      // Fetch schedules (RJ CP data) - CORRECT endpoint: /api/schedules
      const scheduleRes = await axios.get(`${API}/schedules?start_date=${start}&end_date=${end}`, {
        withCredentials: true
      });
      setSchedules(scheduleRes.data || []);

      // Fetch reclamation stats for the same period
      const reclamRes = await axios.get(`${API}/reclamations/stats?date_from=${start}&date_to=${end}&filter_by=livraison`, {
        withCredentials: true
      });
      if (reclamRes.data) {
        setReclamationStats(reclamRes.data);
      }

      // Fetch sites
      const sitesRes = await axios.get(`${API}/sites`, { withCredentials: true });
      // Filter to Colis Privé sites
      const colisSites = (sitesRes.data || []).filter(s => 
        s.name?.toLowerCase().includes('colis') || s.type === 'colis_prive'
      );
      setSites(colisSites);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [getDateRange, viewMode, offset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load driver detail when modal is opened
  const handleDriverClick = async (driver) => {
    setSelectedDriver(driver);
    setShowDriverModal(true);
    setDriverDetailLoading(true);
    
    try {
      // Fetch driver's full history (last 4 weeks + current month)
      const now = new Date();
      const fourWeeksAgo = new Date(now);
      fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);
      
      const historyRes = await axios.get(
        `${API}/schedule/user/${driver.user_id}?start_date=${fourWeeksAgo.toISOString().split('T')[0]}&end_date=${now.toISOString().split('T')[0]}`,
        { withCredentials: true }
      );
      
      // Group by week
      const weeklyHistory = [];
      const schedulesByWeek = {};
      
      (historyRes.data || []).forEach(s => {
        if (!isApprovedWorkedSchedule(s)) return;
        if (s.delivered === null || s.delivered === undefined) return;
        const schedDate = new Date(s.date);
        const weekStart = new Date(schedDate);
        weekStart.setDate(schedDate.getDate() - schedDate.getDay() + 1);
        const weekKey = weekStart.toISOString().split('T')[0];
        
        if (!schedulesByWeek[weekKey]) {
          schedulesByWeek[weekKey] = {
            weekStart: weekKey,
            weekLabel: `Semaine du ${weekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`,
            colis: 0,
            retours: 0,
            nonCollected: 0,
            missions: 0,
            schedules: []
          };
        }
        schedulesByWeek[weekKey].colis += (s.delivered || 0);
        schedulesByWeek[weekKey].retours += (s.returned || 0);
        schedulesByWeek[weekKey].nonCollected += (s.non_collected || 0);
        schedulesByWeek[weekKey].missions += 1;
        schedulesByWeek[weekKey].schedules.push(s);
      });
      
      // Sort by week descending
      const sortedWeeks = Object.values(schedulesByWeek)
        .sort((a, b) => new Date(b.weekStart) - new Date(a.weekStart));
      
      setDriverHistory(sortedWeeks);
      
      // Fetch driver's reclamations
      const reclamRes = await axios.get(
        `${API}/reclamations?livreur_id=${driver.user_id}&limit=50`,
        { withCredentials: true }
      ).catch(() => ({ data: { reclamations: [] } }));
      
      setDriverReclamations(reclamRes.data?.reclamations || reclamRes.data || []);
      
    } catch (error) {
      console.error('Error loading driver details:', error);
    } finally {
      setDriverDetailLoading(false);
    }
  };

  // Close driver modal
  const closeDriverModal = () => {
    setShowDriverModal(false);
    setSelectedDriver(null);
    setDriverHistory([]);
    setDriverReclamations([]);
  };

  // Process schedules into driver statistics
  const driverStats = useMemo(() => {
    // Filter schedules for Colis Privé sites only and with delivery data
    const validSchedules = schedules.filter(s => {
      // Filter by Colis Privé site
      const siteName = (s.site_name || '').toLowerCase();
      const isColisPrive = siteName.includes('colis') || siteName.includes('privé') || siteName.includes('prive');
      
      // Only schedules with delivered data and administratively approved mission
      const hasData = s.delivered !== null && s.delivered !== undefined && isApprovedWorkedSchedule(s);
      
      // Filter by selected site if applicable
      const matchesSite = !selectedSite || s.site_id === selectedSite;
      
      return isColisPrive && hasData && matchesSite;
    });

    // Build reclamation map by user_id
    const reclamationsMap = {};
    (reclamationStats.driver_stats || []).forEach(d => {
      reclamationsMap[d.livreur_id] = {
        total: d.total || 0,
        non_traite: d.non_traite || 0,
        en_cours: d.en_cours || 0,
        traite: d.traite || 0,
        score: d.quality_score || 100
      };
    });

    // Group by user
    const userMap = {};
    validSchedules.forEach(s => {
      if (!userMap[s.user_id]) {
        userMap[s.user_id] = {
          user_id: s.user_id,
          name: s.user_name || s.name || 'Inconnu',
          site_id: s.site_id,
          site_name: s.site_name || 'Non assigné',
          colis_livres: 0,
          colis_retours: 0,
          non_collected: 0,
          picked: 0,
          missions: 0,
          reclamations: 0
        };
      }
      userMap[s.user_id].colis_livres += (s.delivered || 0);
      userMap[s.user_id].colis_retours += (s.returned || 0);
      userMap[s.user_id].non_collected += (s.non_collected || 0);
      userMap[s.user_id].picked += (s.picked || 0);
      userMap[s.user_id].missions += 1;
    });

    // Calculate metrics and integrate reclamations
    const drivers = Object.values(userMap).map(d => {
      // Get reclamations for this driver
      const driverReclamations = reclamationsMap[d.user_id] || { total: 0 };
      const reclamations = driverReclamations.total;
      
      const tauxLivraison = d.colis_livres > 0 
        ? Math.round(((d.colis_livres - d.colis_retours) / d.colis_livres) * 10000) / 100
        : 0;
      const tauxReclamation = d.colis_livres > 0
        ? Math.round((reclamations / d.colis_livres) * 10000) / 100
        : 0;
      const performanceLevel = calculatePerformanceLevel(d.colis_livres);
      const alertLevel = calculateAlertLevel(reclamations);
      const score = calculateScore(d.colis_livres, tauxLivraison, tauxReclamation);

      return {
        ...d,
        reclamations,
        taux_livraison: tauxLivraison,
        taux_reclamation: tauxReclamation,
        performance_level: performanceLevel,
        alert_level: alertLevel,
        score
      };
    });

    // Sort by score descending
    return drivers.sort((a, b) => b.score - a.score);
  }, [schedules, selectedSite, reclamationStats]);

  // Calculate global stats
  const globalStats = useMemo(() => {
    if (driverStats.length === 0) {
      return {
        totalLivreurs: 0,
        totalColis: 0,
        totalRetours: 0,
        totalReclamations: reclamationStats.global_stats?.total_reclamations || 0,
        tauxLivraison: 0
      };
    }

    const totalColis = driverStats.reduce((sum, d) => sum + d.colis_livres, 0);
    const totalRetours = driverStats.reduce((sum, d) => sum + d.colis_retours, 0);
    const tauxLivraison = totalColis > 0 
      ? Math.round(((totalColis - totalRetours) / totalColis) * 10000) / 100
      : 0;

    return {
      totalLivreurs: driverStats.length,
      totalColis,
      totalRetours,
      totalReclamations: reclamationStats.global_stats?.total_reclamations || 0,
      tauxLivraison
    };
  }, [driverStats, reclamationStats]);

  // Calculate performance distribution
  const distribution = useMemo(() => {
    const dist = { excellent: 0, good: 0, normal: 0, insufficient: 0 };
    driverStats.forEach(d => {
      dist[d.performance_level] = (dist[d.performance_level] || 0) + 1;
    });
    return dist;
  }, [driverStats]);

  const handlePrevPeriod = () => setOffset(o => o - 1);
  const handleNextPeriod = () => setOffset(o => Math.min(o + 1, 0));
  const handleCurrentPeriod = () => setOffset(0);

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A]">Statistiques Livreurs</h1>
            <p className="text-gray-500">Performance et qualité des livreurs Colis Privé</p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* View Mode Toggle */}
              <div className="flex rounded-lg border overflow-hidden">
                <Button 
                  variant={viewMode === 'week' ? 'default' : 'ghost'} 
                  size="sm"
                  onClick={() => { setViewMode('week'); setOffset(0); }}
                  className="rounded-none"
                >
                  Semaine
                </Button>
                <Button 
                  variant={viewMode === 'month' ? 'default' : 'ghost'} 
                  size="sm"
                  onClick={() => { setViewMode('month'); setOffset(0); }}
                  className="rounded-none"
                >
                  Mois
                </Button>
              </div>

              {/* Period Navigation */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handlePrevPeriod}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleCurrentPeriod}
                  className="min-w-[200px]"
                >
                  {periodLabel}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleNextPeriod}
                  disabled={offset >= 0}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              {/* Site Filter - only show if more than 1 site available */}
              {sites.length > 1 && (
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <select
                  value={selectedSite}
                  onChange={(e) => setSelectedSite(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Tous les sites</option>
                  {sites.map((site) => (
                    <option key={site.site_id} value={site.site_id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </div>
              )}
              {/* Show site badge if only 1 site (CP admin with single site) */}
              {sites.length === 1 && (
              <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-700 font-medium">
                <Filter className="w-4 h-4" />
                {sites[0].name}
              </div>
              )}

              {/* Refresh */}
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          </div>
        ) : (
          <>
            {/* Stats Summary */}
            <StatsSummary stats={globalStats} onKpiClick={(type) => setDetailView(type)} />

            {/* Podium */}
            {driverStats.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    Podium des meilleurs livreurs
                    <span className="text-xs text-gray-400 font-normal ml-2">(cliquez sur un livreur pour voir sa fiche)</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Podium ranking={driverStats.slice(0, 3)} onDriverClick={handleDriverClick} />
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Performance Distribution */}
              <PerformanceDistribution distribution={distribution} />

              {/* Quality Ranking (Reclamations) */}
              <QualityRanking 
                driverStats={reclamationStats.driver_stats}
                onDriverClick={(id) => {
                  // Find driver in driverStats to get full info
                  const driver = driverStats.find(d => d.user_id === id);
                  if (driver) {
                    handleDriverClick(driver);
                  } else {
                    window.location.href = `/admin/b2b/reclamations?driver=${id}`;
                  }
                }}
              />
            </div>

            {/* Full Delivery Ranking */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Classement complet des livraisons
                  <span className="text-xs text-gray-400 font-normal ml-2">(cliquez sur un livreur pour voir sa fiche)</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DeliveryRanking ranking={driverStats} onDriverClick={handleDriverClick} />
              </CardContent>
            </Card>

            {/* Thresholds Reference */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Seuils de référence</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-2">Performance (colis/semaine)</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                        <span>Très bon: ≥ 575 colis</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500" />
                        <span>Bon: ≥ 480 colis</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-orange-500" />
                        <span>Normal: ≥ 430 colis</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <span>Insuffisant: &lt; 430 colis</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Alertes réclamations (par semaine)</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                        <span>OK: &lt; 4 réclamations</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-orange-500" />
                        <span>À surveiller: 4-6 réclamations</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <span>Critique: ≥ 7 réclamations</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Driver Detail Modal */}
        {showDriverModal && selectedDriver && (
          <DriverDetailModal
            driver={selectedDriver}
            history={driverHistory}
            reclamations={driverReclamations}
            loading={driverDetailLoading}
            onClose={closeDriverModal}
          />
        )}

        {/* KPI Detail View Modal */}
        {detailView && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-xl font-bold">
                  {detailView === 'livreurs' && `Détail des ${globalStats?.totalLivreurs} livreurs actifs`}
                  {detailView === 'colis' && `Détail des ${globalStats?.totalColis?.toLocaleString()} colis`}
                  {detailView === 'taux' && `Analyse du taux de livraison (${globalStats?.tauxLivraison}%)`}
                  {detailView === 'reclamations' && `Détail des ${globalStats?.totalReclamations} réclamations`}
                </h2>
                <button onClick={() => setDetailView(null)} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[calc(80vh-80px)]">
                {detailView === 'livreurs' && (
                  <div className="space-y-3">
                    <p className="text-gray-600 mb-4">
                      Liste de tous les livreurs ayant eu une activité sur cette période. 
                      Cliquez sur un livreur pour voir sa fiche détaillée.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {driverStats.map((d, idx) => (
                        <button 
                          key={d.user_id}
                          onClick={() => { setDetailView(null); handleDriverClick(d); }}
                          className="flex items-center gap-3 p-3 rounded-lg border hover:bg-blue-50 hover:border-blue-300 text-left transition-colors"
                        >
                          <span className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center font-bold text-gray-600">
                            {idx + 1}
                          </span>
                          <div className="flex-1">
                            <p className="font-medium">{d.name}</p>
                            <p className="text-xs text-gray-500">{d.site_name}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPerformanceColor(d.performance_level)}`}>
                            {d.colis_livres} colis
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {detailView === 'colis' && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="p-3 bg-blue-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-700">{globalStats?.totalColis?.toLocaleString()}</p>
                        <p className="text-sm text-blue-600">Colis livrés</p>
                      </div>
                      <div className="p-3 bg-orange-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-orange-700">{globalStats?.totalRetours}</p>
                        <p className="text-sm text-orange-600">Retours</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-green-700">{(globalStats?.totalColis - globalStats?.totalRetours)?.toLocaleString()}</p>
                        <p className="text-sm text-green-600">Livrés nets</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-gray-700">{Math.round((globalStats?.totalColis || 0) / Math.max(1, globalStats?.totalLivreurs))}</p>
                        <p className="text-sm text-gray-600">Moyenne/livreur</p>
                      </div>
                    </div>
                    <h4 className="font-medium">Répartition par livreur</h4>
                    <div className="space-y-2">
                      {driverStats.map(d => (
                        <div key={d.user_id} className="flex items-center gap-2">
                          <button 
                            className="text-blue-600 hover:underline text-sm w-32 truncate text-left"
                            onClick={() => { setDetailView(null); handleDriverClick(d); }}
                          >
                            {d.name}
                          </button>
                          <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 rounded-full"
                              style={{ width: `${(d.colis_livres / globalStats?.totalColis) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-20 text-right">{d.colis_livres} colis</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {detailView === 'taux' && (
                  <div className="space-y-4">
                    <p className="text-gray-600">
                      Le taux de livraison ajusté prend en compte les retours pour donner une vision réaliste de la performance.
                    </p>
                    <div className="p-4 bg-emerald-50 rounded-lg text-center">
                      <p className="text-4xl font-bold text-emerald-700">{globalStats?.tauxLivraison}%</p>
                      <p className="text-emerald-600">Taux de livraison global</p>
                      <p className="text-xs text-gray-500 mt-2">
                        Formule: (Colis livrés - Retours) / Colis livrés × 100
                      </p>
                    </div>
                    <h4 className="font-medium">Taux par livreur</h4>
                    <div className="space-y-2">
                      {driverStats.sort((a, b) => b.taux_livraison - a.taux_livraison).map(d => (
                        <div key={d.user_id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                          <button 
                            className="text-blue-600 hover:underline text-sm w-32 truncate text-left"
                            onClick={() => { setDetailView(null); handleDriverClick(d); }}
                          >
                            {d.name}
                          </button>
                          <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                d.taux_livraison >= 95 ? 'bg-green-500' :
                                d.taux_livraison >= 90 ? 'bg-blue-500' :
                                d.taux_livraison >= 85 ? 'bg-orange-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${d.taux_livraison}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-16 text-right">{d.taux_livraison}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {detailView === 'reclamations' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-3 bg-red-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-red-700">{reclamationStats.global_stats?.non_traite || 0}</p>
                        <p className="text-sm text-red-600">Non traitées</p>
                      </div>
                      <div className="p-3 bg-orange-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-orange-700">{reclamationStats.global_stats?.en_cours || 0}</p>
                        <p className="text-sm text-orange-600">En cours</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-green-700">{(reclamationStats.global_stats?.traite || 0) + (reclamationStats.global_stats?.traite_livreur || 0)}</p>
                        <p className="text-sm text-green-600">Traitées</p>
                      </div>
                    </div>
                    <h4 className="font-medium">Réclamations par livreur</h4>
                    <div className="space-y-2">
                      {(reclamationStats.driver_stats || []).filter(d => d.total > 0).map(d => (
                        <button 
                          key={d.livreur_id}
                          onClick={() => {
                            setDetailView(null);
                            const driver = driverStats.find(x => x.user_id === d.livreur_id);
                            if (driver) handleDriverClick(driver);
                          }}
                          className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded text-left"
                        >
                          <span className="w-32 truncate text-blue-600 hover:underline">{d.livreur_name}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            d.non_traite > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100'
                          }`}>
                            {d.non_traite} non traitée(s)
                          </span>
                          <span className="px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-700">{d.en_cours} en cours</span>
                          <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">{d.traite} traitée(s)</span>
                          <span className="ml-auto font-bold">{d.total} total</span>
                        </button>
                      ))}
                      {(reclamationStats.driver_stats || []).filter(d => d.total > 0).length === 0 && (
                        <p className="text-center text-gray-500 py-4">Aucune réclamation sur cette période</p>
                      )}
                    </div>
                    <div className="pt-4 border-t">
                      <Button 
                        onClick={() => window.location.href = '/admin/b2b/reclamations'}
                        className="w-full"
                      >
                        Voir toutes les réclamations
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminDriverStatistics;
